import { useState, useEffect } from 'react';
import { commissionRunsAPI, usersAPI } from '../services/api';
import Sidebar from '../components/layout/Sidebar';
import toast from 'react-hot-toast';
import { avisoDeFecha, NOTA_NOTAS_DE_CREDITO } from '../utils/comisiones';

// Cortes de comision. Pantalla aparte de Comisiones a proposito: Comisiones es
// el reporte que se vuelve a calcular cada vez que se abre, y esto es el
// registro de lo que ya se pago. Mezclarlas invita a confundir un numero vivo
// con uno congelado, que es el error que mas caro sale aqui.

const dinero = (n) => (Number(n) || 0).toLocaleString('en-US',
  { style: 'currency', currency: 'USD' });
const dia = (s) => s ? String(s).slice(0, 10) : '';

// Tasa efectiva: comision entre base. Guion cuando no hay base, que es el caso
// de quien solo cobra override y no vendio nada: dividir entre cero daria
// Infinity o NaN y en pantalla se leeria como un dato.
const tasa = (comision, base) => {
  const c = Number(comision), b = Number(base);
  if (!isFinite(c) || !isFinite(b) || b === 0) return '—';
  return (c / b * 100).toFixed(2) + '%';
};

// Fechas a la americana, MM/DD/AAAA, para todo lo que se LEE en pantalla.
// NO se usa en tres lugares a proposito: los <input type="date">, que el
// estandar de HTML exige en AAAA-MM-DD; lo que se manda al servidor; y las
// comparaciones de periodos, donde el orden alfabetico de AAAA-MM-DD es el
// orden cronologico y cambiarlo rompe el aviso de traslape.
const usa = (s) => {
  const d = dia(s);
  return /^\d{4}-\d{2}-\d{2}$/.test(d)
    ? d.slice(5, 7) + '/' + d.slice(8, 10) + '/' + d.slice(0, 4)
    : d;
};
const esFecha = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

// Toda la cuenta va en UTC a proposito. Antes era new Date(fecha + 'T00:00:00'),
// que es medianoche LOCAL, y luego toISOString(), que es UTC. En un huso UTC-
// como el de aca coincide por casualidad; en cualquier huso UTC+ devolvia el dia
// ANTERIOR, y entonces la pantalla inventaba huecos en catorcenas que si son
// contiguas y el periodo que propone al abrir quedaba de trece dias pisando uno.
//
// Y devuelve '' en lugar de tronar: toISOString() lanza RangeError con una fecha
// invalida, y esto se llama al pintar la pantalla, asi que un año de cinco
// digitos en el <input type="date"> dejaba la pagina de cortes en blanco.
const masDias = (fecha, n) => {
  if (!esFecha(fecha)) return '';
  const [a, m, d] = String(fecha).split('-').map(Number);
  const t = new Date(Date.UTC(a, m - 1, d));
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};

export default function CommissionRunsPage() {
  const [cortes, setCortes]   = useState([]);
  const [sel, setSel]         = useState(null);
  const [cargando, setCarga]  = useState(true);
  const [generando, setGen]   = useState(false);
  const [vendedores, setVend] = useState([]);
  const [desde, setDesde]     = useState('');
  const [hasta, setHasta]     = useState('');

  const cargar = async () => {
    setCarga(true);
    try {
      const { data } = await commissionRunsAPI.listar();
      setCortes(data);
      proponerPeriodo(data);
    } catch (e) {
      toast.error('No se pudo cargar la lista de cortes');
    } finally { setCarga(false); }
  };

  useEffect(() => {
    cargar();
    usersAPI.salespeople().then(r => setVend(r.data)).catch(() => setVend([]));
  }, []);

  // El periodo nuevo arranca al dia siguiente del ultimo corte y dura catorce
  // dias, que es la cadencia que ya se usa. Asi el camino facil es el correcto.
  const proponerPeriodo = (lista) => {
    const fechas = lista.map(c => dia(c.hasta)).filter(Boolean).sort();
    if (!fechas.length) return;
    const d = masDias(fechas[fechas.length - 1], 1);
    setDesde(d);
    setHasta(masDias(d, 13));
  };

  // Hueco o traslape. Un hueco es una catorcena que nadie cobro; un traslape es
  // comision pagada dos veces. Ninguno de los dos se ve en los totales.
  //
  // Se compara contra TODOS los cortes, no nada mas contra el ultimo. La version
  // anterior daba por hecho que uno siempre avanza hacia adelante, y al capturar
  // catorcenas viejas gritaba "se pagaria dos veces" sobre periodos que no se
  // tocan con nada. Un traslape es que los dos intervalos compartan algun dia;
  // no que la fecha inicial sea anterior al ultimo corte.
  const continuidad = () => {
    // esFecha y no solo "existe": con un año de distinto largo la comparacion de
    // cadenas se invierte ('12026-09-05' < '2026-10-16'), asi que la guardia de
    // desde > hasta no veria un periodo al reves.
    if (!esFecha(desde) || !esFecha(hasta) || desde > hasta) return null;
    const rangos = cortes
      .map(c => ({ desde: dia(c.desde), hasta: dia(c.hasta) }))
      .filter(r => esFecha(r.desde) && esFecha(r.hasta) && r.desde <= r.hasta);
    if (!rangos.length) return null;

    // Si ya existe un corte con exactamente este periodo, no hay nada que
    // advertir: recalcular encima no duplica nada, es el mismo corte. Antes,
    // cancelar el dialogo de Recalcular dejaba esas fechas en el estado y la
    // pantalla gritaba "se pagaria dos veces" sobre un corte que ya estaba.
    if (rangos.some(r => r.desde === desde && r.hasta === hasta)) return null;

    // Traslape de verdad: los dos intervalos comparten por lo menos un dia.
    const choca = rangos.find(r => r.desde <= hasta && desde <= r.hasta);
    if (choca)
      return { tipo: 'traslape', texto: 'Se traslapa con el corte del ' + choca.desde + ' al ' + choca.hasta + '. Lo que caiga en los dias repetidos se pagaria dos veces.' };

    // Hueco contra el corte vecino, del lado que sea: hacia atras si se esta
    // capturando una catorcena vieja, hacia adelante si se esta avanzando.
    const atras = rangos.map(r => r.hasta).filter(h => h < desde).sort();
    const previo = atras[atras.length - 1];
    if (previo && masDias(previo, 1) !== desde)
      return { tipo: 'hueco', texto: 'Queda un hueco sin cubrir entre ' + masDias(previo, 1) + ' y ' + masDias(desde, -1) + '. Eso es cobranza que nadie va a comisionar.' };

    const adelante = rangos.map(r => r.desde).filter(d => d > hasta).sort();
    const siguiente = adelante[0];
    if (siguiente && masDias(hasta, 1) !== siguiente)
      return { tipo: 'hueco', texto: 'Queda un hueco sin cubrir entre ' + masDias(hasta, 1) + ' y ' + masDias(siguiente, -1) + '. Eso es cobranza que nadie va a comisionar.' };

    return null;
  };

  const abrir = async (id) => {
    try {
      const { data } = await commissionRunsAPI.ver(id);
      setSel(data);
    } catch { toast.error('No se pudo abrir el corte'); }
  };

  const generar = async () => {
    if (!desde || !hasta) return toast.error('Pon las dos fechas');
    if (desde > hasta)    return toast.error('La fecha inicial va antes de la final');
    const aviso = continuidad();
    const avisoFecha = avisoDeFecha(desde);
    const ok = window.confirm(
      'Generar el corte del ' + desde + ' al ' + hasta + '?\n\n' +
      (aviso ? 'OJO: ' + aviso.texto + '\n\n' : '') +
      (avisoFecha ? 'NOTA: ' + avisoFecha + '\n\n' : '') +
      'Consulta Odoo factura por factura, asi que tarda varios minutos. ' +
      'No cierres la pagina.'
    );
    if (!ok) return;
    setGen(true);
    try {
      const { data } = await commissionRunsAPI.crear(desde, hasta);
      toast.success('Corte generado con ' + data.renglones + ' renglones');
      await cargar();
      await abrir(data.run_id);
    } catch (err) {
      const d = err.response?.data;
      toast.error(d?.error ? d.error + (d.sugerencia ? '. ' + d.sugerencia : '') : 'No se pudo generar');
    } finally { setGen(false); }
  };

  const recalcular = async (c) => {
    const ok = window.confirm(
      'Volver a calcular el corte del ' + usa(c.desde) + ' al ' + usa(c.hasta) + '?\n\n' +
      (avisoDeFecha(dia(c.desde)) ? 'NOTA: ' + avisoDeFecha(dia(c.desde)) + '\n\n' : '') +
      'Se borran los renglones actuales y se vuelven a traer de Odoo. Los ajustes ' +
      'capturados NO se borran. Tarda varios minutos.'
    );
    if (!ok) return;
    // Hasta aqui no se toca el estado de las fechas: moverlas antes del
    // confirm dejaba la pantalla con el periodo del corte viejo si la persona
    // cancelaba, y de ahi salia la advertencia falsa de doble pago.
    setDesde(dia(c.desde)); setHasta(dia(c.hasta));
    setGen(true);
    try {
      const { data } = await commissionRunsAPI.crear(dia(c.desde), dia(c.hasta));
      toast.success('Recalculado: ' + data.renglones + ' renglones');
      await cargar();
      await abrir(data.run_id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo recalcular');
    } finally { setGen(false); }
  };

  if (cargando) return (
    <div className="app-shell"><Sidebar />
      <div className="main-content"><div className="loading-center"><div className="spinner" /></div></div>
    </div>
  );

  if (sel) return (
    <Detalle corte={sel} vendedores={vendedores}
             onVolver={() => { setSel(null); cargar(); }}
             onRecargar={() => abrir(sel.id)} />
  );

  const aviso = continuidad();
  const avisoFecha = avisoDeFecha(desde);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div><h2>Cortes de comision</h2></div>
        </div>

        <div className="page-body">
          <div className="card" style={{ marginBottom: 18 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Nuevo corte</h3>
            <p className="text-muted text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
              Se calcula sobre lo cobrado en el periodo. Queda abierto: los numeros
              se pueden volver a calcular y se le pueden capturar ajustes. Hasta que
              se cierra, se congela y ya nadie lo puede mover.
            </p>

            <div style={{
              marginBottom: 14, padding: '9px 12px', borderRadius: 6, fontSize: 12.5,
              lineHeight: 1.6, color: 'var(--text-2)',
              background: 'var(--bg)', border: '1px dashed var(--border)',
            }}>
              {NOTA_NOTAS_DE_CREDITO}
            </div>
            <div className="flex gap-3 items-end" style={{ marginTop: 14, flexWrap: 'wrap' }}>
              <div>
                <label className="form-label">Del</label>
                <input type="date" className="form-input" style={{ width: 170 }}
                       value={desde} onChange={e => setDesde(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Al</label>
                <input type="date" className="form-input" style={{ width: 170 }}
                       value={hasta} onChange={e => setHasta(e.target.value)} />
              </div>
              <button className="btn btn-primary" onClick={generar} disabled={generando}>
                {generando ? 'Calculando, tarda minutos...' : 'Generar corte'}
              </button>
            </div>

            {avisoFecha && (
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 6, fontSize: 13,
                lineHeight: 1.6, color: 'var(--text-2)',
                background: 'var(--bg)', border: '1px solid var(--border)',
              }}>
                {avisoFecha}
              </div>
            )}
            {aviso && (
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 6, fontSize: 13,
                lineHeight: 1.6, color: 'var(--text)',
                background: 'var(--bg)', border: '1px solid var(--orange, #b45309)',
              }}>
                {aviso.tipo === 'hueco' ? 'Hueco en la secuencia. ' : 'Periodos traslapados. '}
                {aviso.texto}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Periodo</th><th>Estado</th><th>Renglones</th><th>Ajustes</th>
                    <th>Generado</th><th>Cerrado por</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {cortes.length === 0 && (
                    <tr><td colSpan={7} className="text-muted" style={{ padding: 20 }}>
                      Todavia no hay cortes. Genera el primero arriba.
                    </td></tr>
                  )}
                  {cortes.map(c => (
                    <tr key={c.id}>
                      <td style={{ color: 'var(--text)', fontWeight: 600 }}>
                        {usa(c.desde)} al {usa(c.hasta)}
                      </td>
                      <td>
                        {c.estado === 'cerrado'
                          ? <span className="badge badge-gray">Cerrado</span>
                          : <span className="badge badge-green">Abierto</span>}
                      </td>
                      <td>{c.renglones}</td>
                      <td>{c.ajustes}</td>
                      <td className="text-muted">{usa(c.generado_at)}</td>
                      <td className="text-muted">
                        {c.cerrado_por ? c.cerrado_por + '  ' + usa(c.cerrado_at) : ''}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-ghost btn-sm" onClick={() => abrir(c.id)}>Ver</button>
                          {c.estado !== 'cerrado' && (
                            <button className="btn btn-ghost btn-sm" disabled={generando}
                                    onClick={() => recalcular(c)}>Recalcular</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -- DETALLE DE UN CORTE ---------------------------------------------
function Detalle({ corte, vendedores, onVolver, onRecargar }) {
  const cerrado = corte.estado === 'cerrado';
  const [notas, setNotas] = useState('');
  const [cerrando, setCerrando] = useState(false);
  const [verRevisar, setVerRevisar] = useState(false);
  const [verRenglones, setVerRenglones] = useState(false);
  // Cual ajuste se esta editando. null = ninguno, el formulario esta en modo captura.
  // Se guarda el ID, no el objeto. Con el objeto, borrar otro ajuste recargaba
  // el corte y el formulario se quedaba con la foto vieja, y borrar el que se
  // estaba editando dejaba el formulario atorado sobre una fila que ya no
  // existe. Derivado del corte fresco, los dos casos se resuelven solos.
  const [editandoId, setEditandoId] = useState(null);
  const editando = (corte.ajustes || []).find(a => a.id === editandoId) || null;

  const cerrar = async () => {
    const ok = window.confirm(
      'Cerrar el corte del ' + usa(corte.desde) + ' al ' + usa(corte.hasta) + '?\n\n' +
      'Se congelan ' + dinero(granTotal) + ' en total, sobre ' +
      (corte.renglones || []).length + ' renglones' +
      ((corte.revisar || []).length
        ? ', y quedan ' + corte.revisar.length + ' renglones marcados PARA REVISAR'
        : '') + '.\n\n' +
      'Esto NO se puede deshacer. Los totales quedan congelados y ni el sistema ' +
      'ni nadie los puede cambiar despues, aunque Odoo cambie.\n\n' +
      'Cualquier correccion posterior entra como ajuste en el corte siguiente.'
    );
    if (!ok) return;
    setCerrando(true);
    try {
      await commissionRunsAPI.cerrar(corte.id, notas);
      toast.success('Corte cerrado');
      onRecargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo cerrar');
    } finally { setCerrando(false); }
  };

  const borrarAjuste = async (a) => {
    if (!window.confirm('Borrar el ajuste de ' + dinero(a.monto) + ' a ' + a.vendedor + '?')) return;
    try {
      await commissionRunsAPI.borrarAjuste(corte.id, a.id);
      if (editandoId === a.id) setEditandoId(null);
      toast.success('Ajuste borrado');
      onRecargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo borrar');
    }
  };

  const avisoFecha = avisoDeFecha(dia(corte.desde));
  const totales = corte.totales || [];
  const granTotal = totales.reduce((s, t) => s + (Number(t.total_a_pagar) || 0), 0);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div>
            <h2>Corte {usa(corte.desde)} al {usa(corte.hasta)}</h2>
            <span className="text-muted text-sm">
              {cerrado
                ? 'Cerrado por ' + (corte.cerrado_por_nombre || '?') + ' el ' + usa(corte.cerrado_at) + '. Los totales estan congelados.'
                : 'Abierto. Los totales se recalculan con cada ajuste que captures.'}
            </span>
          </div>
          <button className="btn btn-ghost" onClick={onVolver}>Volver a la lista</button>
        </div>

        <div className="page-body">
          {avisoFecha && (
            <div style={{
              marginTop: 14, padding: '10px 12px', borderRadius: 6, fontSize: 13,
              lineHeight: 1.6, color: 'var(--text-2)',
              background: 'var(--bg)', border: '1px solid var(--border)',
            }}>
              {avisoFecha}
            </div>
          )}
          <div className="card" style={{ padding: 0, marginBottom: 18 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Vendedor</th><th>Cobrado</th><th>Base</th><th>Comision</th>
                    <th>Tasa prom.</th>
                    <th>Ajustes</th><th>Override generado</th><th>Override a cobrar</th>
                    <th>Total a pagar</th>
                  </tr>
                </thead>
                <tbody>
                  {totales.map(t => (
                    <tr key={t.vendedor}>
                      <td style={{ color: 'var(--text)', fontWeight: 600 }}>{t.vendedor}</td>
                      <td>{dinero(t.cobrado)}</td>
                      <td>{dinero(t.base)}</td>
                      <td>{dinero(t.comision)}</td>
                      {/* Tasa efectiva del periodo: comision entre base. Sin los
                          ajustes a proposito, porque los ajustes corrigen OTROS
                          periodos y ensuciarian la tasa de este. Se lee sabiendo
                          que mezcla familias y origenes: si alguien vende mas
                          aluminio que fierro un mes, su promedio baja sin que
                          nada este mal. Sirve para preguntar, no para concluir. */}
                      <td className="text-muted">{tasa(t.comision, t.base)}</td>
                      <td style={{ color: Number(t.ajustes) ? 'var(--orange, #b45309)' : 'inherit' }}>
                        {dinero(t.ajustes)}
                      </td>
                      {/* Lo que las ventas de esta persona generaron PARA OTRO.
                          No es dinero de ella, por eso va en gris. */}
                      <td className="text-muted">{dinero(t.override)}</td>
                      {/* Lo que esta persona SE LLEVA del override de todos. En
                          los cortes cerrados antes del 20 de octubre el campo no
                          existe, y ahi va un guion en lugar de un cero: en esos
                          el override entro como ajuste capturado a mano. */}
                      <td style={{ color: Number(t.override_a_cobrar) ? 'var(--text)' : 'inherit',
                                   fontWeight: Number(t.override_a_cobrar) ? 600 : 400 }}>
                        {t.override_a_cobrar === undefined ? '—' : dinero(t.override_a_cobrar)}
                      </td>
                      <td style={{ color: 'var(--text)', fontWeight: 700 }}>{dinero(t.total_a_pagar)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'right', fontWeight: 600 }}>Total</td>
                    <td style={{ color: 'var(--text)', fontWeight: 700 }}>{dinero(granTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* El override se explica aqui porque es la columna que mas se ha
              malentendido: se genera en las ventas de todos y se le paga a una
              sola persona. */}
          {!cerrado && (
            <div className="card" style={{ marginBottom: 18 }}>
              <p className="text-muted text-sm" style={{ margin: 0, lineHeight: 1.6 }}>
                <strong>El override.</strong> La columna <em>generado</em> es lo que las ventas de cada
                vendedor produjeron para otro; no es dinero suyo. La columna <em>a cobrar</em> es lo que
                esa persona se lleva. Desde el 20 de octubre el total a pagar ya lo incluye solo:{' '}
                <strong>ya no hay que capturarlo como ajuste.</strong> Si se captura, se paga dos veces.
                {corte.override_lo_cobra
                  ? ' En este corte lo cobra ' + corte.override_lo_cobra + '.'
                  : ''}
              </p>
            </div>
          )}

          {/* Si hay override generado y nadie lo cobra, el corte esta corto y
              hay que decirlo, no dejar que el total se vea completo. */}
          {!cerrado && !corte.override_lo_cobra
            && totales.some(t => Number(t.override)) && (
            <div className="card" style={{
              marginBottom: 18, borderColor: 'var(--orange, #b45309)',
            }}>
              <p className="text-sm" style={{ margin: 0, lineHeight: 1.6, color: 'var(--text)' }}>
                <strong>Este corte genero override y no se le esta sumando a nadie.</strong> El sistema
                no pudo decidir quien lo cobra: necesita exactamente una tarifa con rol de override
                vigente a la fecha de cierre, y hoy hay cero o mas de una. El total de abajo esta corto
                por {dinero(totales.reduce((s, t) => s + (Number(t.override) || 0), 0))}. Hay que
                arreglar las tarifas antes de cerrar, o capturarlo como ajuste a mano.
              </p>
            </div>
          )}

          {corte.revisar && corte.revisar.length > 0 && (
            <div className="card" style={{ marginBottom: 18 }}>
              <div className="flex justify-between items-center">
                <h3 style={{ margin: 0, fontSize: 15 }}>
                  Para revisar ({corte.revisar.length})
                </h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setVerRevisar(v => !v)}>
                  {verRevisar ? 'Ocultar' : 'Ver'}
                </button>
              </div>
              <p className="text-muted text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
                Renglones donde el motor no pudo decidir solo y aplico un criterio
                por omision. Revisalos antes de cerrar: despues ya no se corrigen
                aqui, van como ajuste en el corte siguiente.
              </p>
              {verRevisar && (
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table>
                    <thead>
                      <tr><th>Fecha</th><th>Cliente</th><th>Factura</th><th>Vendedor</th>
                          <th>Monto</th><th>Comision</th><th>Por que</th></tr>
                    </thead>
                    <tbody>
                      {corte.revisar.map((x, i) => (
                        <tr key={i}>
                          <td className="text-muted">{usa(x.fecha)}</td>
                          <td>{x.cliente}</td>
                          <td className="text-muted">{x.factura || x.so}</td>
                          <td>{x.vendedor}</td>
                          <td>{dinero(x.monto)}</td>
                          <td>{dinero(x.comision)}</td>
                          <td className="text-sm" style={{ color: 'var(--orange, #b45309)' }}>
                            {String(x.decidir || '').split(' | ').join('; ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="card" style={{ marginBottom: 18 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>
              Ajustes ({(corte.ajustes || []).length})
            </h3>
            <p className="text-muted text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
              Aqui van las correcciones de periodos anteriores ya cerrados. Cada una
              pide un motivo porque dentro de seis meses nadie se va a acordar de
              por que ese vendedor cobro de mas o de menos.
            </p>
            <p className="text-muted text-sm" style={{ marginTop: 4, lineHeight: 1.6 }}>
              <strong>El signo:</strong> positivo se le paga de mas, negativo se le
              descuenta. Escribe la factura y el resto se llena solo.
            </p>

            {(corte.ajustes || []).length > 0 && (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr><th>Vendedor</th><th>Monto</th><th>Override</th><th>Corrige</th>
                        <th>Referencia</th><th>Motivo</th><th>Capturo</th><th></th></tr>
                  </thead>
                  <tbody>
                    {corte.ajustes.map(a => (
                      <tr key={a.id}
                          style={editandoId === a.id ? { background: 'var(--bg)' } : undefined}>
                        <td style={{ color: 'var(--text)', fontWeight: 600 }}>{a.vendedor}</td>
                        <td style={{ color: Number(a.monto) < 0 ? 'var(--red)' : 'inherit' }}>
                          {dinero(a.monto)}
                        </td>
                        <td style={{ color: Number(a.ovr_monto) < 0 ? 'var(--red)' : 'inherit' }}>
                          {dinero(a.ovr_monto)}
                        </td>
                        <td className="text-muted text-sm">
                          {a.corrige_desde ? usa(a.corrige_desde) + ' al ' + usa(a.corrige_hasta) : ''}
                        </td>
                        <td className="text-muted text-sm">{a.factura || a.so || a.cliente || ''}</td>
                        <td className="text-sm">{a.motivo}</td>
                        <td className="text-muted text-sm">{a.capturo}</td>
                        <td>
                          {!cerrado && (
                            <div className="flex gap-2">
                              <button className="btn btn-ghost btn-sm"
                                      onClick={() => setEditandoId(a.id)}>Editar</button>
                              <button className="btn btn-ghost btn-sm"
                                      style={{ color: 'var(--red)' }}
                                      onClick={() => borrarAjuste(a)}>Borrar</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {cerrado
              ? <p className="text-muted text-sm" style={{ marginTop: 12 }}>
                  El corte esta cerrado: no se pueden capturar ni borrar ajustes.
                  Lo que falte va en el corte siguiente.
                </p>
              : <FormAjuste corteId={corte.id} vendedores={vendedores}
                            ajuste={editando}
                            onCerrar={() => setEditandoId(null)}
                            onGuardado={() => { setEditandoId(null); onRecargar(); }} />}
          </div>

          <div className="card" style={{ marginBottom: 18 }}>
            <div className="flex justify-between items-center">
              <h3 style={{ margin: 0, fontSize: 15 }}>
                Detalle completo ({(corte.renglones || []).length} renglones)
              </h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setVerRenglones(v => !v)}>
                {verRenglones ? 'Ocultar' : 'Ver'}
              </button>
            </div>
            {verRenglones && (
              <div className="table-wrap" style={{ marginTop: 12, maxHeight: 520, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr><th>Fecha</th><th>Tipo</th><th>Cliente</th><th>Factura</th>
                        <th>Familia</th><th>Vendedor</th><th>%</th><th>Cobrado</th>
                        <th>Base</th><th>Comision</th><th>Override</th></tr>
                  </thead>
                  <tbody>
                    {(corte.renglones || []).map((x, i) => (
                      <tr key={i}>
                        <td className="text-muted">{usa(x.fecha)}</td>
                        <td className="text-muted text-sm">{x.tipo}</td>
                        <td>{x.cliente}</td>
                        <td className="text-muted text-sm">{x.factura || x.so}</td>
                        <td className="text-muted text-sm">{x.familia}</td>
                        <td>{x.vendedor}</td>
                        <td>{x.pct != null ? (Number(x.pct) * 100).toFixed(2) + '%' : ''}</td>
                        <td>{dinero(x.monto)}</td>
                        <td>{dinero(x.base)}</td>
                        <td>{dinero(x.comision)}</td>
                        <td>{dinero(x.ovr_monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!cerrado && (
            <div className="card">
              <h3 style={{ margin: 0, fontSize: 15 }}>Cerrar el corte</h3>
              <p className="text-muted text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
                Al cerrar, los totales de arriba se guardan tal como estan y quedan
                intocables. Esto es lo que hace que un corte pagado siga cuadrando
                dentro de un ano, aunque alguien edite facturas en Odoo. No se puede
                deshacer.
              </p>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Nota del cierre (opcional pero recomendada)</label>
                <input className="form-input" value={notas}
                       placeholder="Por ejemplo: pagado el 15 de octubre, incluye ajuste de Balsam"
                       onChange={e => setNotas(e.target.value)} />
              </div>
              <button className="btn btn-primary" onClick={cerrar}
                      disabled={cerrando || totales.length === 0}>
                {cerrando ? 'Cerrando...' : 'Cerrar corte definitivamente'}
              </button>
            </div>
          )}

          {cerrado && corte.notas && (
            <div className="card">
              <h3 style={{ margin: 0, fontSize: 15 }}>Nota del cierre</h3>
              <p className="text-sm" style={{ marginTop: 8, lineHeight: 1.6, color: 'var(--text)' }}>
                {corte.notas}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -- CAPTURA Y EDICION DE UN AJUSTE ----------------------------------
//
// Regla de captura del proyecto: si el sistema ya tiene el dato, la persona no
// lo teclea. Se escribe la factura, el SO o el cliente, y de ahi salen el
// vendedor, la base, el porcentaje y el PERIODO que se esta corrigiendo.
//
// Lo de las fechas no es comodidad, es correccion: antes Corrige se llenaba a
// mano y terminaba con el periodo del corte donde se captura, que en la
// operacion normal es el equivocado. Un ajuste de hoy corrige el anterior.
function FormAjuste({ corteId, vendedores, onGuardado, ajuste, onCerrar }) {
  const vacio = {
    user_id: '', monto: '', ovr_monto: '', base: '', pct: '',
    so: '', factura: '', cliente: '', corrige_desde: '', corrige_hasta: '', motivo: '',
  };
  const deAjuste = (a) => ({
    user_id: a.user_id != null ? String(a.user_id) : '',
    monto: a.monto != null ? String(a.monto) : '',
    ovr_monto: a.ovr_monto != null ? String(a.ovr_monto) : '',
    base: a.base != null ? String(a.base) : '',
    pct: a.pct != null ? String(a.pct) : '',
    so: a.so || '', factura: a.factura || '', cliente: a.cliente || '',
    corrige_desde: dia(a.corrige_desde), corrige_hasta: dia(a.corrige_hasta),
    motivo: a.motivo || '',
  });

  const editMode = !!ajuste;
  const [f, setF] = useState(vacio);
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [q, setQ] = useState('');
  const [sug, setSug] = useState([]);
  const [buscando, setBuscando] = useState(false);
  // El renglon que se escogio, para poder avisar y para dejarlo a la vista.
  const [elegido, setElegido] = useState(null);
  // null = todavia no se busca. 'vacio' = no hay. 'error' = fallo la consulta.
  // Los tres se veian igual, y un error de red se leia como "no existe".
  const [resultado, setResultado] = useState(null);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  // Entrar o salir de edicion carga el formulario. Sin esto, darle Editar a un
  // ajuste mientras otro estaba a medias dejaba los campos del anterior.
  useEffect(() => {
    if (ajuste) { setF(deAjuste(ajuste)); setAbierto(true); setQ(''); setSug([]); setElegido(null); setResultado(null); }
  }, [ajuste]);

  // Busqueda con pausa: se dispara 350 ms despues de dejar de teclear, para no
  // mandar una consulta por letra.
  useEffect(() => {
    const texto = q.trim();
    if (texto.length < 3) { setSug([]); setResultado(null); setBuscando(false); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await commissionRunsAPI.buscarRenglon(texto);
        if (vivo) { setSug(data); setResultado(data.length ? 'hay' : 'vacio'); }
      } catch { if (vivo) { setSug([]); setResultado('error'); } }
      finally { if (vivo) setBuscando(false); }
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [q]);

  const tomar = (r) => {
    // Si el renglon no resolvio a un vendedor vigente y unico, el campo se
    // LIMPIA. Heredar el que estuviera escogido antes dejaba el formulario
    // viendose completo y correcto con la persona equivocada.
    setElegido(r);
    setF(x => ({
      ...x,
      user_id: r.user_id != null ? String(r.user_id) : '',
      so: r.so || '', factura: r.factura || '', cliente: r.cliente || '',
      base: r.base != null ? String(r.base) : '',
      pct:  r.pct  != null ? String(r.pct)  : '',
      corrige_desde: r.corrige_desde || '',
      corrige_hasta: r.corrige_hasta || '',
    }));
    setSug([]); setQ('');
    toast.success('Se llenaron el vendedor, la base, el porcentaje y el periodo desde ' +
                  (r.factura || r.so || 'el renglon'));
  };

  // Los avisos que afectan dinero NO van como toast: el toast se va y la
  // condicion se queda. Se derivan del renglon escogido y viven en pantalla
  // hasta que se resuelven.
  const avisos = [];
  if (elegido) {
    if (elegido.empates === 0)
      avisos.push('El renglon dice "' + (elegido.vendedor || '?') + '" y ese nombre no esta en la ' +
                  'lista de vendedores vigentes. Escoge tu a quien se le ajusta.');
    else if (elegido.empates > 1)
      avisos.push('Hay ' + elegido.empates + ' vendedores vigentes que se llaman "' +
                  (elegido.vendedor || '?') + '". Escoge tu cual es.');
    if (String(elegido.run_id) === String(corteId))
      avisos.push('Ese renglon es del corte que estas capturando, no de uno anterior. ' +
                  'Normalmente un ajuste corrige un corte ya cerrado. Si es a proposito, adelante.');
  }

  const guardar = async () => {
    if (!f.user_id) return toast.error('Escoge al vendedor');
    if (f.monto === '' || isNaN(Number(f.monto))) return toast.error('El monto tiene que ser un numero');
    if (f.ovr_monto !== '' && isNaN(Number(f.ovr_monto)))
      return toast.error('El override tiene que ser un numero, sin comas ni signo de pesos');
    if (f.motivo.trim().length < 10) return toast.error('El motivo necesita al menos 10 caracteres');

    // El periodo que se corrige es el campo que estuvo mal durante meses sin que
    // nadie lo notara. El backend lo acepta vacio o al reves y lo guarda en
    // NULL sin decir nada, asi que la guardia va aqui.
    if (f.corrige_desde && f.corrige_hasta && f.corrige_hasta < f.corrige_desde)
      return toast.error('El periodo que se corrige esta al reves: el "al" va antes del "del"');
    if (!f.corrige_desde || !f.corrige_hasta) {
      const sigue = window.confirm(
        'Este ajuste va a quedar SIN el periodo que corrige.\n\n' +
        'Ese dato es lo que permite auditarlo despues. Lo normal es escoger el ' +
        'renglon arriba para que se llene solo.\n\nGuardar asi de todos modos?');
      if (!sigue) return;
    }

    const quien = (vendedores.find(v => String(v.id) === String(f.user_id)) || {}).name || 'ese vendedor';
    const n = Number(f.monto);
    const ok = window.confirm(
      (n < 0 ? 'Se le DESCUENTAN ' + dinero(Math.abs(n)) : 'Se le PAGAN DE MAS ' + dinero(n)) +
      ' a ' + quien + '.' +
      (Number(f.ovr_monto) ? '\nOverride: ' + dinero(Number(f.ovr_monto)) + '.' : '') +
      '\n\nCorrige el periodo ' + (f.corrige_desde || '(sin fecha)') + ' al ' +
      (f.corrige_hasta || '(sin fecha)') + '.');
    if (!ok) return;

    setGuardando(true);
    try {
      if (editMode) {
        await commissionRunsAPI.editarAjuste(corteId, ajuste.id, f);
        toast.success('Ajuste actualizado');
      } else {
        await commissionRunsAPI.agregarAjuste(corteId, f);
        toast.success('Ajuste capturado');
      }
      setF(vacio); setAbierto(false); setQ(''); setSug([]); setElegido(null); setResultado(null);
      onGuardado();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo guardar');
    } finally { setGuardando(false); }
  };

  const cancelar = () => {
    setF(vacio); setAbierto(false); setQ(''); setSug([]); setElegido(null); setResultado(null);
    if (onCerrar) onCerrar();
  };

  if (!abierto && !editMode) return (
    <button className="btn btn-ghost" style={{ marginTop: 12 }}
            onClick={() => setAbierto(true)}>+ Capturar ajuste</button>
  );

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      {editMode && (
        <p className="text-sm" style={{ marginTop: 0, marginBottom: 12, color: 'var(--orange, #b45309)' }}>
          Editando el ajuste de {dinero(ajuste.monto)} a {ajuste.vendedor}.
        </p>
      )}

      <div className="form-group">
        <label className="form-label">
          Buscar el renglon (factura, SO o cliente)
        </label>
        <input className="form-input" value={q} autoComplete="off"
               placeholder="INV/2026/0535, SO12883 o Balsam"
               onChange={e => setQ(e.target.value)} />
        <p className="text-muted text-sm" style={{ marginTop: 4 }}>
          {buscando
            ? 'Buscando...'
            : 'Escribe al menos tres letras. Busca en todos los cortes, abiertos y cerrados.'}
        </p>
        {!buscando && resultado === 'vacio' && (
          <div style={{ marginTop: 8, padding: '9px 12px', borderRadius: 6, fontSize: 13,
                        lineHeight: 1.6, color: 'var(--text)', background: 'var(--bg)',
                        border: '1px solid var(--border)' }}>
            No hay ningun renglon con "{q.trim()}" en ningun corte. Si el ajuste no sale
            de una factura comisionada, llena los campos a mano y explica en el motivo
            de donde viene.
          </div>
        )}
        {!buscando && resultado === 'error' && (
          <div style={{ marginTop: 8, padding: '9px 12px', borderRadius: 6, fontSize: 13,
                        lineHeight: 1.6, color: 'var(--text)', background: 'var(--bg)',
                        border: '1px solid var(--red)' }}>
            No se pudo buscar. Vuelve a intentar: esto NO significa que el renglon no exista.
          </div>
        )}
        {!buscando && sug.length === 25 && (
          <p className="text-sm" style={{ marginTop: 6, color: 'var(--orange, #b45309)' }}>
            Son 25 y puede haber mas. Escribe el numero completo de la factura.
          </p>
        )}

        {sug.length > 0 && (
          <div className="table-wrap" style={{ marginTop: 8, maxHeight: 240, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr><th>Periodo</th><th>Fecha</th><th>Factura</th><th>Cliente</th>
                    <th>Vendedor</th><th>Familia</th><th>%</th><th>Base</th>
                    <th>Comision</th><th></th></tr>
              </thead>
              <tbody>
                {sug.map(r => (
                  <tr key={r.id}>
                    <td className="text-muted text-sm">
                      {usa(r.corrige_desde)} al {usa(r.corrige_hasta)}
                      {r.run_estado === 'cerrado' ? '' : (
                        <strong style={{ color: 'var(--orange, #b45309)' }}> ABIERTO</strong>
                      )}
                    </td>
                    <td className="text-muted text-sm">{usa(r.fecha)}</td>
                    <td className="text-muted text-sm">{r.factura || r.so}</td>
                    <td>{r.cliente}</td>
                    <td>{r.vendedor}{r.empates === 0 ? ' (no esta en la lista)' : ''}</td>
                    <td className="text-muted text-sm">{r.familia}</td>
                    <td>{r.pct != null ? (Number(r.pct) * 100).toFixed(2) + '%' : ''}</td>
                    <td>{dinero(r.base)}</td>
                    <td>{dinero(r.comision)}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm"
                              onClick={() => tomar(r)}>Llenar con este</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {elegido && (
        <div style={{ marginBottom: 12, padding: '9px 12px', borderRadius: 6, fontSize: 13,
                      lineHeight: 1.6, color: 'var(--text-2)', background: 'var(--bg)',
                      border: '1px dashed var(--border)' }}>
          Renglon: <strong>{elegido.factura || elegido.so}</strong> · {elegido.cliente} ·
          {' '}{elegido.vendedor} · periodo {usa(elegido.corrige_desde)} al {usa(elegido.corrige_hasta)}
          {' '}· comision {dinero(elegido.comision)}
          {elegido.pct != null && <> · {(Number(elegido.pct) * 100).toFixed(2)}%</>}
        </div>
      )}

      {avisos.map((a, i) => (
        <div key={i} style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6,
                              fontSize: 13, lineHeight: 1.6, color: 'var(--text)',
                              background: 'var(--bg)',
                              border: '1px solid var(--orange, #b45309)' }}>
          {a}
        </div>
      ))}

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Vendedor *</label>
          <select className="form-select" value={f.user_id}
                  onChange={e => set('user_id', e.target.value)}>
            <option value="">Escoge...</option>
            {vendedores.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Monto *  (negativo si se le descuenta)</label>
          <input className="form-input" value={f.monto}
                 placeholder="-271.14" onChange={e => set('monto', e.target.value)} />
          <p className="text-sm" style={{ marginTop: 4, color: 'var(--text)' }}>
            {f.monto === '' || isNaN(Number(f.monto))
              ? 'Positivo se le paga de mas, negativo se le descuenta.'
              : (Number(f.monto) < 0
                  ? 'Se le DESCUENTAN ' + dinero(Math.abs(Number(f.monto))) + '.'
                  : 'Se le PAGAN DE MAS ' + dinero(Number(f.monto)) + '.')}
          </p>
        </div>
        <div className="form-group">
          <label className="form-label">Override  (mismo signo)</label>
          <input className="form-input" value={f.ovr_monto}
                 placeholder="0.00" onChange={e => set('ovr_monto', e.target.value)} />
          <p className="text-muted text-sm" style={{ marginTop: 4 }}>
            Solo si el ajuste mueve el override. Mismo signo que el monto.
          </p>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Corrige del</label>
          <input type="date" className="form-input" value={f.corrige_desde}
                 onChange={e => set('corrige_desde', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">al</label>
          <input type="date" className="form-input" value={f.corrige_hasta}
                 onChange={e => set('corrige_hasta', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Factura</label>
          <input className="form-input" value={f.factura}
                 placeholder="INV/2026/0123" onChange={e => set('factura', e.target.value)} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">SO</label>
          <input className="form-input" value={f.so}
                 placeholder="SO12345" onChange={e => set('so', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Cliente</label>
          <input className="form-input" value={f.cliente}
                 onChange={e => set('cliente', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Base del renglon</label>
          <input className="form-input" value={f.base}
                 placeholder="3873.36" onChange={e => set('base', e.target.value)} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          Motivo *  — explica que se esta corrigiendo y en que corte  ({f.motivo.length} / 500)
        </label>
        <textarea className="form-input" rows={3} value={f.motivo} maxLength={500}
               style={{ minHeight: 72, height: 'auto', resize: 'vertical' }}
               placeholder="Balsam quedo con origen casa en el corte del 19 de septiembre y era cuenta propia"
               onChange={e => set('motivo', e.target.value)} />
      </div>

      <div className="flex gap-2">
        <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
          {guardando ? 'Guardando...' : (editMode ? 'Guardar cambios' : 'Guardar ajuste')}
        </button>
        <button className="btn btn-ghost" onClick={cancelar}>Cancelar</button>
      </div>
    </div>
  );
}
